/**
 * appStore — global application state (Zustand)
 *
 * Manages: theme, language, sidebar collapse, accent color, active category.
 * All persisted to localStorage.
 */

import { create } from 'zustand';
import i18n from '@/i18n';
import type { VisualPatternId } from '@/lib/portal-theme';
import { resolveBooxCiSet, resolveBooxIconSet, type BooxCiSetId, type BooxIconSetId } from '@/lib/visual-preferences';

export type Theme = 'light' | 'dark' | 'system';
export type Language = 'en' | 'zh' | 'zh-Hant' | 'id' | 'ms' | 'ja' | 'th' | 'vi';
export type AccentColor = 'blue' | 'rose' | 'pink' | 'purple' | 'teal' | 'green' | 'amber' | 'indigo' | 'orange' | 'slate';

/**
 * DesignMode controls whether users see the Modern (new) or Classic (legacy WebForms-style) layout.
 * - 'modern'  → default; DSL-driven panels, assist drawer, right dock
 * - 'classic' → compact 2-column flat layout; all fields visible on main page, no assist panel
 *
 * Stored in localStorage under 'boox_design_mode'. Sets `data-design-mode` on <html> so
 * CSS can apply Classic-mode overrides (e.g. legacy status colours, narrow grid spacing).
 */
export type DesignMode = 'modern' | 'classic';

interface AppState {
  // ── Theme ──
  theme: Theme;
  setTheme: (theme: Theme) => void;

  // ── Language ──
  language: Language;
  setLanguage: (lang: Language) => void;

  // ── Sidebar ──
  sidebarCollapsed: boolean;
  toggleSidebar: () => void;

  // ── Accent color ──
  accent: AccentColor;
  setAccent: (accent: AccentColor) => void;

  // ── PWA background pattern ──
  pwaVisualPattern: VisualPatternId;
  setPwaVisualPattern: (pattern: VisualPatternId) => void;

  // ── PWA CI / icon sets ──
  pwaCiSet: BooxCiSetId;
  setPwaCiSet: (ciSet: BooxCiSetId) => void;
  pwaIconSet: BooxIconSetId;
  setPwaIconSet: (iconSet: BooxIconSetId) => void;

  // ── Active sidebar category ──
  activeCategory: string | null;
  setActiveCategory: (cat: string | null) => void;

  // ── Chat panel ──
  chatPanelOpen: boolean;
  toggleChatPanel: () => void;
  setChatPanelOpen: (open: boolean) => void;

  // ── Design mode (Modern vs Classic legacy layout) ──
  designMode: DesignMode;
  setDesignMode: (mode: DesignMode) => void;

  // ── Per-screen template overrides ──
  // Key: '${entitySlug}:${viewType}', Value: registered template id (e.g. 'classic', 'ops-focus')
  // Persisted in localStorage under 'boox_screen_templates'.
  // Takes priority over the global designMode for that specific screen.
  screenTemplates: Record<string, string>;
  setScreenTemplate: (slug: string, viewType: string, templateId: string) => void;
  clearScreenTemplate: (slug: string, viewType: string) => void;
}

function applyThemeToDOM(theme: Theme) {
  const root = document.documentElement;
  if (theme === 'system') {
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    root.classList.toggle('dark', prefersDark);
  } else {
    root.classList.toggle('dark', theme === 'dark');
  }
}

function applyAccentToDOM(accent: AccentColor) {
  document.documentElement.setAttribute('data-accent', accent);
}

function applyDesignModeToDOM(mode: DesignMode) {
  document.documentElement.setAttribute('data-design-mode', mode);
}

const storedTheme = (localStorage.getItem('boox_theme') as Theme) ?? 'system';
const storedLang = (localStorage.getItem('boox_lang') as Language) ?? 'en';
const storedCollapsed = localStorage.getItem('boox_sidebar_collapsed') === 'true';
const storedAccent = (localStorage.getItem('boox_accent') as AccentColor) ?? 'blue';
const storedPwaVisualPattern = (localStorage.getItem('boox_pwa_visual_pattern') as VisualPatternId) ?? 'minimal_grid';
const storedPwaCiSet = resolveBooxCiSet(localStorage.getItem('boox_pwa_ci_set'));
const storedPwaIconSet = resolveBooxIconSet(localStorage.getItem('boox_pwa_icon_set'));
const storedCategory = localStorage.getItem('boox_active_category');
const storedDesignMode = (localStorage.getItem('boox_design_mode') as DesignMode) ?? 'modern';
const storedScreenTemplates: Record<string, string> = JSON.parse(localStorage.getItem('boox_screen_templates') ?? '{}');

// Apply initial states on load
applyThemeToDOM(storedTheme);
applyAccentToDOM(storedAccent);
applyDesignModeToDOM(storedDesignMode);

// Listen for system theme changes when mode is 'system'
window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
  const current = useAppStore.getState().theme;
  if (current === 'system') applyThemeToDOM('system');
});

export const useAppStore = create<AppState>((set) => ({
  // ── Theme ──
  theme: storedTheme,
  setTheme: (theme) => {
    localStorage.setItem('boox_theme', theme);
    applyThemeToDOM(theme);
    set({ theme });
  },

  // ── Language ──
  language: storedLang,
  setLanguage: (lang) => {
    localStorage.setItem('boox_lang', lang);
    void i18n.changeLanguage(lang);
    set({ language: lang });
  },

  // ── Sidebar ──
  sidebarCollapsed: storedCollapsed,
  toggleSidebar: () =>
    set((s) => {
      const next = !s.sidebarCollapsed;
      localStorage.setItem('boox_sidebar_collapsed', String(next));
      return { sidebarCollapsed: next };
    }),

  // ── Accent color ──
  accent: storedAccent,
  setAccent: (accent) => {
    localStorage.setItem('boox_accent', accent);
    applyAccentToDOM(accent);
    set({ accent });
  },

  // ── PWA background pattern ──
  pwaVisualPattern: storedPwaVisualPattern,
  setPwaVisualPattern: (pattern) => {
    localStorage.setItem('boox_pwa_visual_pattern', pattern);
    set({ pwaVisualPattern: pattern });
  },

  // ── PWA CI / icon sets ──
  pwaCiSet: storedPwaCiSet,
  setPwaCiSet: (ciSet) => {
    localStorage.setItem('boox_pwa_ci_set', ciSet);
    set({ pwaCiSet: ciSet });
  },
  pwaIconSet: storedPwaIconSet,
  setPwaIconSet: (iconSet) => {
    localStorage.setItem('boox_pwa_icon_set', iconSet);
    set({ pwaIconSet: iconSet });
  },

  // ── Active sidebar category ──
  activeCategory: storedCategory,
  setActiveCategory: (cat) => {
    if (cat) localStorage.setItem('boox_active_category', cat);
    else localStorage.removeItem('boox_active_category');
    set({ activeCategory: cat });
  },

  // ── Chat panel ──
  chatPanelOpen: false,
  toggleChatPanel: () => set((s) => ({ chatPanelOpen: !s.chatPanelOpen })),
  setChatPanelOpen: (open) => set({ chatPanelOpen: open }),

  // ── Design mode ──
  designMode: storedDesignMode,
  setDesignMode: (mode) => {
    localStorage.setItem('boox_design_mode', mode);
    applyDesignModeToDOM(mode);
    set({ designMode: mode });
  },

  // ── Per-screen template overrides ──
  screenTemplates: storedScreenTemplates,
  setScreenTemplate: (slug, viewType, templateId) => {
    set((s) => {
      const next = { ...s.screenTemplates, [`${slug}:${viewType}`]: templateId };
      localStorage.setItem('boox_screen_templates', JSON.stringify(next));
      return { screenTemplates: next };
    });
  },
  clearScreenTemplate: (slug, viewType) => {
    set((s) => {
      const next = { ...s.screenTemplates };
      delete next[`${slug}:${viewType}`];
      localStorage.setItem('boox_screen_templates', JSON.stringify(next));
      return { screenTemplates: next };
    });
  },
}));
